"use client";

import React from "react";
import { Briefcase, Building, ChevronRight, Cpu, Info, Shield, Terminal } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
const personas = [{
  id: "founders",
  title: "Founders",
  iconName: "Briefcase",
  level: "Root Access (L0)",
  metric: "Context Retention: 99.9%",
  description: "Unrestricted access to strategic memory and core system configurations."
}, {
  id: "agencies",
  title: "Agencies",
  iconName: "Building",
  level: "Multi-Tenant Admin (L1)",
  metric: "Concurrent Threads: 256+",
  description: "Isolated workspace management with strict cross-client boundaries."
}, {
  id: "operators",
  title: "Operators",
  iconName: "Terminal",
  level: "Workflow Architect (L2)",
  metric: "Execution Latency: <50ms",
  description: "Direct toolchain integration and custom iMessage command mapping."
}, {
  id: "executive",
  title: "Executive Systems",
  iconName: "Shield",
  level: "Autonomous Agent (L3)",
  metric: "Approval Confidence: High",
  description: "Delegated execution with mandatory human-in-the-loop approval gates."
}];
const useCases = [{
  personaId: "founders",
  text: "Automated Investor Reporting"
}, {
  personaId: "founders",
  text: "Private Knowledge Retrieval"
}, {
  personaId: "founders",
  text: "Strategic Context Memory"
}, {
  personaId: "agencies",
  text: "Cross-Client Context Switching"
}, {
  personaId: "agencies",
  text: "Automated Asset Generation"
}, {
  personaId: "agencies",
  text: "Multi-Tenant Permission Sandboxing"
}, {
  personaId: "operators",
  text: "iMessage-Triggered CI/CD"
}, {
  personaId: "operators",
  text: "Local File System Operations"
}, {
  personaId: "operators",
  text: "Custom Tool Chain Integration"
}, {
  personaId: "executive",
  text: "Meeting Synthesis & Actioning"
}, {
  personaId: "executive",
  text: "Secure Document Parsing"
}, {
  personaId: "executive",
  text: "Asynchronous Approval Workflows"
}];
const getIcon = (name: string) => {
  switch (name) {
    case "Briefcase":
      return <Briefcase className="size-5 text-primary" />;
    case "Building":
      return <Building className="size-5 text-primary" />;
    case "Terminal":
      return <Terminal className="size-5 text-primary" />;
    case "Shield":
      return <Shield className="size-5 text-primary" />;
    default:
      return <Cpu className="size-5 text-primary" />;
  }
};
export default function OperatorUseCases() {
  return <section id="operator-use-cases" className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-10 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="mb-16 max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
            Deployment Profiles
          </h2>
          <p className="text-muted-foreground text-lg">
            Configured operational modes for specific organizational roles. 
            Hover over profile levels to view technical constraints and metrics.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {personas.map((persona, i) => {
          const personaUseCases = useCases.filter(uc => uc.personaId === persona.id);
          return <Card key={persona.id} data-index={i} className="bg-card/40 backdrop-blur-sm border-border/50 hover:border-primary/30 transition-colors duration-300">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-background rounded-md border border-border/50 shadow-sm">
                      {getIcon(persona.iconName)}
                    </div>
                    <CardTitle className="text-xl font-semibold tracking-tight">
                      {persona.title}
                    </CardTitle>
                  </div>
                  
                  <HoverCard>
                    <HoverCardTrigger asChild>
                      <button className="inline-flex items-center justify-center rounded-full size-8 hover:bg-accent hover:text-accent-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                        <Info className="size-4 text-muted-foreground" />
                        <span className="sr-only">View operator level details</span>
                      </button>
                    </HoverCardTrigger>
                    <HoverCardContent align="end" className="w-80 bg-popover/95 backdrop-blur-md border-border shadow-xl">
                      <div className="space-y-3">
                        <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                          <Terminal className="size-3 text-primary" />
                          Operator Level
                        </h4>
                        <div className="text-xs font-mono bg-muted/50 p-2 rounded-md text-muted-foreground border border-border/50">
                          <div className="mb-1">
                            <span className="text-foreground/70">Class:</span> {persona.level}
                          </div>
                          <div>
                            <span className="text-foreground/70">SLA:</span> {persona.metric}
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {persona.description}
                        </p>
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                </CardHeader>
                
                <CardContent>
                  <ul className="space-y-3 mt-4">
                    {personaUseCases.map((useCase, j) => <li key={`${persona.id}-uc-${j}`} data-index={j} className="flex items-start gap-2 text-sm text-secondary-foreground">
                        <ChevronRight className="size-4 text-primary/70 shrink-0 mt-0.5" />
                        <span className="leading-tight">{useCase.text}</span>
                      </li>)}
                  </ul>
                </CardContent>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}